import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Grid,
  Typography,
  Stack,
  Box,
  Paper,
  TextField,
  Divider,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import SnackbarInfo from '../common/snackbar-info.component';
import { updateDataById } from '../../services/videos.service';
import Collections from '../form-elements/collections.component';
import Playlists from '../form-elements/playlists.component';
import Channels from '../form-elements/channels.component';
import Genres from '../form-elements/genres.component';
import RokuPrimaryGenres from '../form-elements/roku-primary-genres.component';
import AlwaysPublished from '../form-elements/always-published.component';
import Datepicker from '../form-elements/datepicker.component';
import { formatDate } from '../../utils/datetime.util';

const VideoManageAssignments = ({
  videoDetail,
  type,
  handleRefreshData,
  formValueChangedCallback,
}) => {
  const {
    control,
    formState: { errors, dirtyFields },
    handleSubmit,
  } = useForm({
    defaultValues: {
      collections: videoDetail.collectionId
        ? {
            id: videoDetail.collectionId,
            name: videoDetail.collectionName || '',
          }
        : null,
      playlists: videoDetail.playlists || [],
      channels: videoDetail.channels || [],
      genres: videoDetail.genres || [],
      primaryGenres: videoDetail.primaryGenres || [],
      alwaysPublished: videoDetail.alwaysPublished || 'NO',
      randomRank: videoDetail.randomRank,
    },
  });
  const heroSchedulingRef = useRef({
    isDateChange: false,
    startDate: videoDetail?.heroPlaylistStartDate || null,
    endDate: videoDetail?.heroPlaylistEndDate || null,
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });

  const onFormSubmit = (data) => {
    setFormSubmitted(true);
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    const payload = {
      ...data,
      collections: data?.collections?.id || null,
      playlists: data?.playlists?.map((item) => item.id) || [],
      channels: data?.channels?.map((item) => item.id) || [],
      genres: data?.genres?.map((item) => item.id) || [],
      primaryGenres: data?.primaryGenres?.map((item) => item.id) || [],
      type,
    };

    if (heroSchedulingRef.current.isDateChange) {
      payload.startDate = heroSchedulingRef.current.startDate;
      payload.endDate = heroSchedulingRef.current.endDate;
    }
    updateDataById(videoDetail.id, payload)
      .then(() => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'success',
          message: 'Assignment data updated successfully.',
        });
        heroSchedulingRef.isDateChange = false;
        formValueChangedCallback(false);
        handleRefreshData();
        setFormSubmitted(false);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setFormSubmitted(false);
      });
  };

  useEffect(() => {
    if (Object.keys(dirtyFields).length > 0) {
      formValueChangedCallback(true);
    } else {
      formValueChangedCallback(false);
    }
  }, [Object.keys(dirtyFields).length]);

  const handleDateChange = (date, action) => {
    heroSchedulingRef.current.isDateChange = true;

    const inputDate = formatDate(date, 'YYYY-MM-DD');
    if (action === 'start' && inputDate) {
      heroSchedulingRef.current.startDate = inputDate;
    } else if (action === 'end' && inputDate) {
      heroSchedulingRef.current.endDate = inputDate;
    }
  };

  return (
    <Box component={Paper} padding={2}>
      <form id="video-assignments-form" onSubmit={handleSubmit(onFormSubmit)}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={12}>
            <Controller
              name="channels"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Channels
                  id="channels"
                  name="channels"
                  label="Channels"
                  defaultValue={value}
                  multiple
                  onChange={onChange}
                  sx={{ width: '100%' }}
                  error={errors?.channels?.message || ''}
                  disabled={videoDetail.videoSource === 'YOUTUBE'}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={12} md={12}>
            <Controller
              name="collections"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Collections
                  id="collections"
                  name="collections"
                  label="Collections"
                  defaultValue={value}
                  onChange={onChange}
                  sx={{ width: '100%' }}
                  error={errors?.collections?.message || ''}
                  disabled={videoDetail.videoSource === 'YOUTUBE'}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={12} md={12}>
            <Controller
              name="playlists"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Playlists
                  id="playlists"
                  name="playlists"
                  label="Playlists"
                  defaultValue={value}
                  multiple
                  onChange={onChange}
                  sx={{ width: '100%' }}
                  error={errors?.playlists?.message || ''}
                  firstLevelPlaylist
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={12} md={12}>
            <Controller
              name="genres"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Genres
                  id="genres"
                  name="genres"
                  label="Genres"
                  defaultValue={value}
                  multiple
                  onChange={onChange}
                  sx={{ width: '100%' }}
                  error={errors?.genres?.message || ''}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="primaryGenres"
              control={control}
              render={({ field: { onChange, value } }) => (
                <RokuPrimaryGenres
                  id="primary_genres"
                  name="primaryGenres"
                  label="Primary Genres"
                  defaultValue={value}
                  multiple
                  onChange={onChange}
                  sx={{ width: '100%' }}
                  error={errors?.primaryGenres?.message || ''}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="alwaysPublished"
              control={control}
              render={({ field: { onChange, value } }) => (
                <AlwaysPublished
                  id="always_published"
                  name="always_published"
                  label="Always Published"
                  defaultValue={value}
                  onChange={onChange}
                  sx={{ width: '100%' }}
                  error={errors?.alwaysPublished?.message || ''}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name="randomRank"
              control={control}
              disabled
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Random Rank"
                  type="text"
                  error={!!errors.randomRank}
                  helperText={errors?.randomRank?.message || null}
                  fullWidth
                  variant="filled"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} />
          <Grid item xs={12}>
            <Box sx={{ mt: 2, mb: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Manual Hero Playlist Scheduling
              </Typography>
              <Divider sx={{ mt: 1 }} />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Datepicker
              id="hero_playlist_start_date"
              name="hero_playlist_start_date"
              label="Start Date"
              defaultValue={videoDetail?.heroPlaylistStartDate || null}
              onChange={(e) => handleDateChange(e, 'start')}
              sx={{ width: 370 }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Datepicker
              id="hero_playlist_end_date"
              name="hero_playlist_end_date"
              label="End Date"
              defaultValue={videoDetail?.heroPlaylistEndDate || null}
              onChange={(e) => handleDateChange(e, 'end')}
              sx={{ width: 370 }}
            />
          </Grid>
        </Grid>
      </form>

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ py: 2.5 }}
      >
        <Button
          type="submit"
          form="video-assignments-form"
          color="primary"
          variant="contained"
          disabled={formSubmitted}
        >
          Save Assignment
        </Button>
        <Typography variant="body2" color="secondary">
          * indicates a required field
        </Typography>
      </Stack>

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </Box>
  );
};

VideoManageAssignments.propTypes = {
  videoDetail: PropTypes.object.isRequired,
  type: PropTypes.string.isRequired,
  handleRefreshData: PropTypes.func.isRequired,
  formValueChangedCallback: PropTypes.func.isRequired,
};

export default VideoManageAssignments;
